/** Local application roles. Permissions per role are defined in `ability.ts` (PRD §3.1). */
export const ROLE_CODES = ['admin', 'noc', 'planner', 'teknisi'] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

/**
 * Service area (wilayah). `code` must match the technician roleCode in Speedmax ID SSO.
 * Areas are master data (table `areas`); admins add new ones without code changes (PRD SET-05).
 */
export interface AreaInfo {
  code: string;
  name: string;
  /** Asset code segment, e.g. `CPK` in `ODP-CPK-0012`. */
  prefix: string;
  color: string | null;
}
export type AreaCode = string;

/** The four areas seeded at install. Runtime lists come from the database. */
export const DEFAULT_AREAS: readonly AreaInfo[] = [
  { code: 'cipongkor', name: 'Cipongkor', prefix: 'CPK', color: '#fb923c' },
  { code: 'lampungtimur', name: 'Lampung Timur', prefix: 'LTM', color: '#a3e635' },
  { code: 'muktiwari', name: 'Muktiwari', prefix: 'MKW', color: '#e879f9' },
  { code: 'pamegarsari', name: 'Pamegarsari', prefix: 'PMS', color: '#2dd4bf' },
];

/** Area codes are SSO roleCodes: lower-case letters and digits. */
export const AREA_CODE_PATTERN = /^[a-z][a-z0-9]{1,39}$/;

let areaDirectory: readonly AreaInfo[] = DEFAULT_AREAS;

/** Replaces the known areas (API: from the database; web: from `GET /api/v1/areas/directory`). */
export function setAreaDirectory(list: readonly AreaInfo[]): void {
  areaDirectory = list;
}

export function areaList(): readonly AreaInfo[] {
  return areaDirectory;
}

export function areaInfo(code: string): AreaInfo | undefined {
  return areaDirectory.find((a) => a.code === code);
}

export function areaName(code: string): string {
  return areaInfo(code)?.name ?? code;
}

/**
 * Seed mapping from SSO `roles[].roleCode` to a local role and area scope.
 * `area: null` means every area. The database table `sso_role_mappings` is the
 * runtime source of truth; this constant seeds it and backs the unit tests.
 */
export const DEFAULT_SSO_ROLE_MAPPINGS: ReadonlyArray<{
  ssoRoleCode: string;
  role: RoleCode;
  area: AreaCode | null;
}> = [
  // `administrator` is the FTTH NMS app role. The SSO's own `admin` role (managing Speedmax ID) grants nothing here.
  { ssoRoleCode: 'administrator', role: 'admin', area: null },
  { ssoRoleCode: 'noc', role: 'noc', area: null },
  { ssoRoleCode: 'planner', role: 'planner', area: null },
  { ssoRoleCode: 'cipongkor', role: 'teknisi', area: 'cipongkor' },
  { ssoRoleCode: 'lampungtimur', role: 'teknisi', area: 'lampungtimur' },
  { ssoRoleCode: 'muktiwari', role: 'teknisi', area: 'muktiwari' },
  { ssoRoleCode: 'pamegarsari', role: 'teknisi', area: 'pamegarsari' },
];

/** Asset types with stable codes (never rely on numeric ids). Layer drives the map colour. */
export const ASSET_TYPES = [
  { code: 'OLT', name: 'OLT', layer: 'core', icon: 'server', codePrefix: 'OLT' },
  { code: 'SERVER', name: 'Server / Router', layer: 'core', icon: 'router', codePrefix: 'RTR' },
  { code: 'AP', name: 'Access Point', layer: 'core', icon: 'wifi', codePrefix: 'AP' },
  { code: 'ODC', name: 'ODC', layer: 'distribution', icon: 'box', codePrefix: 'ODC' },
  { code: 'ODP', name: 'ODP', layer: 'access', icon: 'git-fork', codePrefix: 'ODP' },
  { code: 'JC', name: 'Joint Closure', layer: 'passive', icon: 'link', codePrefix: 'JC' },
  { code: 'POLE', name: 'Tiang Tumpu', layer: 'passive', icon: 'utility-pole', codePrefix: 'TT' },
  { code: 'CPE', name: 'Pelanggan (ONT/HTB)', layer: 'customer', icon: 'house', codePrefix: 'CPE' },
] as const;
export type AssetTypeCode = (typeof ASSET_TYPES)[number]['code'];
export const ASSET_TYPE_CODES = ASSET_TYPES.map((t) => t.code) as AssetTypeCode[];

export function assetTypeName(code: string): string {
  return ASSET_TYPES.find((t) => t.code === code)?.name ?? code;
}

/** Automatic asset code prefix, e.g. `ODP-CPK` → `ODP-CPK-0012`. */
export function assetCodePrefix(typeCode: string, areaCode: string, areaPrefix?: string | null): string {
  const t = ASSET_TYPES.find((x) => x.code === typeCode)?.codePrefix ?? typeCode;
  const a = areaPrefix || areaInfo(areaCode)?.prefix || areaCode.slice(0, 3).toUpperCase();
  return `${t}-${a}`;
}

export const ASSET_STATUSES = ['planned', 'active', 'inactive', 'maintenance', 'decommissioned'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  planned: 'Rencana',
  active: 'Aktif',
  inactive: 'Nonaktif',
  maintenance: 'Maintenance',
  decommissioned: 'Dibongkar',
};

/** TIA-598 fibre colour order (core/tube 1..12). */
export const FIBER_COLORS = [
  { order: 1, name: 'Biru', hex: '#0072bc' },
  { order: 2, name: 'Oranye', hex: '#f7941d' },
  { order: 3, name: 'Hijau', hex: '#00a651' },
  { order: 4, name: 'Coklat', hex: '#8b5e3c' },
  { order: 5, name: 'Abu-abu', hex: '#8a8d8f' },
  { order: 6, name: 'Putih', hex: '#f4f4f4' },
  { order: 7, name: 'Merah', hex: '#ed1c24' },
  { order: 8, name: 'Hitam', hex: '#231f20' },
  { order: 9, name: 'Kuning', hex: '#fff200' },
  { order: 10, name: 'Ungu', hex: '#92278f' },
  { order: 11, name: 'Merah muda', hex: '#f49ac2' },
  { order: 12, name: 'Tosca', hex: '#00b5d8' },
] as const;

/** Splitter ratios and typical insertion loss (dB). Seeds `splitter_types`; used for loss estimates. */
export const SPLITTERS = [
  { category: 'main', ratio: '1:2', lossDb: 3.7 },
  { category: 'main', ratio: '1:4', lossDb: 7.3 },
  { category: 'main', ratio: '1:8', lossDb: 10.5 },
  { category: 'main', ratio: '1:16', lossDb: 13.8 },
  { category: 'odp', ratio: '1:2', lossDb: 3.7 },
  { category: 'odp', ratio: '1:4', lossDb: 7.3 },
  { category: 'odp', ratio: '1:8', lossDb: 10.5 },
  { category: 'odp', ratio: '1:16', lossDb: 13.8 },
  { category: 'odp', ratio: '1:32', lossDb: 17.1 },
] as const;

export function splitterLossDb(ratio: string | null | undefined): number | null {
  if (!ratio) return null;
  return SPLITTERS.find((s) => s.ratio === ratio)?.lossDb ?? null;
}

/** Typical G.652D fibre loss used when no measurement exists. */
export const FIBER_LOSS_DB_PER_KM = 0.35;
